import type {
  WorkflowSourceFields,
  WorkflowTaskDetail,
  WorkflowTaskReference,
} from "../services/regulationsService";

function asRecord(value: unknown): Record<string, unknown> | undefined {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  return value as Record<string, unknown>;
}

function isWorkflowTaskReference(item: unknown): item is WorkflowTaskReference {
  return item != null && typeof item === "object" && !Array.isArray(item);
}

function referenceDedupeKey(item: WorkflowTaskReference): string {
  return [
    item.field ?? "",
    item.uri ?? "",
    item.value ?? "",
    item.name ?? "",
  ].join("|");
}

/** Parse `references` when backend sends a JSON string instead of an array. */
export function coerceReferencesArray(value: unknown): WorkflowTaskReference[] {
  if (Array.isArray(value)) {
    return value.filter(isWorkflowTaskReference);
  }
  if (typeof value !== "string" || value.trim() === "") return [];
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed)
      ? parsed.filter(isWorkflowTaskReference)
      : [];
  } catch {
    return [];
  }
}

function mergeReferenceLists(
  target: WorkflowTaskReference[],
  seen: Set<string>,
  candidate: unknown,
): void {
  for (const item of coerceReferencesArray(candidate)) {
    const key = referenceDedupeKey(item);
    if (seen.has(key)) continue;
    seen.add(key);
    target.push(item);
  }
}

/** Deep-scan an API payload and collect every `references` array/item. */
export function collectReferencesDeepFromPayload(
  root: unknown,
  maxDepth = 8,
): WorkflowTaskReference[] {
  const merged: WorkflowTaskReference[] = [];
  const seen = new Set<string>();

  const walk = (obj: unknown, depth: number): void => {
    if (depth >= maxDepth || obj == null) return;

    if (Array.isArray(obj)) {
      for (const entry of obj) walk(entry, depth + 1);
      return;
    }

    if (typeof obj !== "object") return;

    const record = obj as Record<string, unknown>;
    mergeReferenceLists(merged, seen, record.references);

    for (const value of Object.values(record)) {
      if (value != null && typeof value === "object") {
        walk(value, depth + 1);
      }
    }
  };

  walk(root, 0);
  return merged;
}

/**
 * Live Olympus nests `previousTask` / `previousTaskDisplayMode` under `taskPage`.
 * Also accept root-level siblings for older/mock shapes.
 */
export function resolvePreviousTaskFromTaskDetail(
  taskDetail: WorkflowTaskDetail | null | undefined,
): WorkflowTaskDetail["previousTask"] {
  if (!taskDetail) return null;
  if (taskDetail.previousTask != null) return taskDetail.previousTask;
  const nested = (
    taskDetail.taskPage as { previousTask?: WorkflowTaskDetail["previousTask"] } | undefined
  )?.previousTask;
  return nested ?? null;
}

function isSourceFieldsShape(value: unknown): value is WorkflowSourceFields {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return "enabled" in record || "items" in record;
}

/**
 * Live Olympus nests `sourceFields` under `task` (or occasionally `taskPage`).
 * Accept root-level siblings for older/mock shapes.
 */
export function resolveSourceFieldsFromTaskDetail(
  taskDetail: WorkflowTaskDetail | null | undefined,
): WorkflowSourceFields | null {
  if (!taskDetail) return null;
  if (isSourceFieldsShape(taskDetail.sourceFields)) {
    return taskDetail.sourceFields;
  }
  const fromTask = asRecord(taskDetail.task)?.sourceFields;
  if (isSourceFieldsShape(fromTask)) return fromTask;
  const fromPage = asRecord(taskDetail.taskPage)?.sourceFields;
  if (isSourceFieldsShape(fromPage)) return fromPage;
  return null;
}

/** Unwrap accidental `{ data: WorkflowTaskDetail }` nesting inside stored payloads. */
export function normalizeWorkflowTaskDetailPayload(
  raw: unknown,
): WorkflowTaskDetail | null {
  if (raw == null || typeof raw !== "object") return null;
  const record = raw as Record<string, unknown>;
  if (!record.task && record.data != null && typeof record.data === "object") {
    return normalizeWorkflowTaskDetailPayload(record.data);
  }

  const detail = record as WorkflowTaskDetail;
  const taskPage = asRecord(detail.taskPage);
  const nestedPrevious = taskPage?.previousTask;
  const nestedMode = taskPage?.previousTaskDisplayMode;

  // Lift taskPage.previousTask* to root so form/summary consumers share one path.
  return {
    ...detail,
    previousTask:
      detail.previousTask ??
      (nestedPrevious as WorkflowTaskDetail["previousTask"]) ??
      null,
    previousTaskDisplayMode:
      detail.previousTaskDisplayMode ??
      (typeof nestedMode === "string" || nestedMode == null
        ? (nestedMode as WorkflowTaskDetail["previousTaskDisplayMode"])
        : detail.previousTaskDisplayMode),
    sourceFields: resolveSourceFieldsFromTaskDetail(detail),
  };
}

function pickRicherTaskPage(
  current: Record<string, unknown> | undefined,
  incoming: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!incoming) return current;
  if (!current) return incoming;

  const currentRefs = coerceReferencesArray(current.references);
  const incomingRefs = coerceReferencesArray(incoming.references);
  const references =
    incomingRefs.length > currentRefs.length ? incomingRefs : currentRefs;

  return {
    ...current,
    ...incoming,
    references,
  };
}

/** Merge sibling/nested `data` layers so empty outer `taskPage.references` does not win. */
function mergeWorkflowTaskDetailLayers(
  primary: WorkflowTaskDetail | null,
  secondary: WorkflowTaskDetail | null,
): WorkflowTaskDetail | null {
  if (!primary) return secondary;
  if (!secondary) return primary;

  const primaryPage = asRecord(primary.taskPage);
  const secondaryPage = asRecord(secondary.taskPage);
  const taskPage = pickRicherTaskPage(primaryPage, secondaryPage);

  const primaryRootRefs = coerceReferencesArray(primary.references);
  const secondaryRootRefs = coerceReferencesArray(secondary.references);
  const references =
    secondaryRootRefs.length > primaryRootRefs.length
      ? secondaryRootRefs
      : primaryRootRefs;

  return {
    ...primary,
    ...secondary,
    task: { ...asRecord(secondary.task), ...asRecord(primary.task) },
    taskPage: taskPage as WorkflowTaskDetail["taskPage"],
    references: references.length > 0 ? references : primary.references,
    previousTask:
      primary.previousTask ?? secondary.previousTask ?? null,
    previousTaskDisplayMode:
      primary.previousTaskDisplayMode ??
      secondary.previousTaskDisplayMode ??
      null,
    sourceFields:
      resolveSourceFieldsFromTaskDetail(primary) ??
      resolveSourceFieldsFromTaskDetail(secondary) ??
      null,
  };
}

function extractTaskDetailCandidate(raw: unknown): WorkflowTaskDetail | null {
  const normalized = normalizeWorkflowTaskDetailPayload(raw);
  if (!normalized?.task) return null;
  return normalized;
}

function resolveTaskDetailFromEnvelope(envelope: unknown): WorkflowTaskDetail | null {
  if (envelope == null || typeof envelope !== "object") return null;

  const record = envelope as Record<string, unknown>;
  let detail = extractTaskDetailCandidate(record.data ?? record);

  const nestedData = asRecord(record.data)?.data;
  if (nestedData != null) {
    detail = mergeWorkflowTaskDetailLayers(
      detail,
      extractTaskDetailCandidate(nestedData),
    );
  }

  return detail;
}

function injectReferencesIntoTaskDetail(
  detail: WorkflowTaskDetail,
  references: WorkflowTaskReference[],
): WorkflowTaskDetail {
  if (references.length === 0) return detail;

  const taskPageRecord = asRecord(detail.taskPage) ?? {};
  const existingTaskPageRefs = coerceReferencesArray(taskPageRecord.references);
  const existingRootRefs = coerceReferencesArray(detail.references);

  const seen = new Set<string>();
  const merged: WorkflowTaskReference[] = [];
  for (const list of [existingTaskPageRefs, existingRootRefs, references]) {
    mergeReferenceLists(merged, seen, list);
  }

  return {
    ...detail,
    references: merged,
    taskPage: {
      ...taskPageRecord,
      references: merged,
    },
  };
}

/**
 * Normalize GET workflow task API response:
 * - unwrap nested `data`
 * - deep-collect `references` from the full envelope
 * - prefer non-empty `taskPage.references`
 */
export function parseWorkflowTaskDetailFromApiResponse(
  envelope: unknown,
): WorkflowTaskDetail | null {
  const detail = resolveTaskDetailFromEnvelope(envelope);
  if (!detail) return null;

  const deepReferences = collectReferencesDeepFromPayload(envelope);
  return injectReferencesIntoTaskDetail(detail, deepReferences);
}

/** @deprecated Use parseWorkflowTaskDetailFromApiResponse */
export function mergeEnvelopeReferencesIntoTaskDetail(
  envelope: unknown,
  detail: WorkflowTaskDetail | null,
): WorkflowTaskDetail | null {
  if (!detail) return parseWorkflowTaskDetailFromApiResponse(envelope);
  const deepReferences = collectReferencesDeepFromPayload(envelope);
  return injectReferencesIntoTaskDetail(detail, deepReferences);
}
